@if (footerStore.isLoading() || !footerStore.hasLoaded() || !footerForm) {
  <div class="pt-32">
    <aw-loading-animation></aw-loading-animation>
  </div>
} @else {
  @let hasChanges = hasUnsavedChanges(footerForm);
  <div
    class="-mx-16 flex h-full min-h-0 flex-col"
    [formGroup]="footerForm"
    data-testid="section-footer">
    <div class="flex shrink-0 flex-col gap-4 bg-white px-21 pt-5 pb-4">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.shared.footer }}
        </aw-section-headline>
        <aw-section-header-actions>
          <aw-reveal-form-errors-button [form]="footerForm" />
          <aw-button
            [id]="'discard-changes'"
            [style]="'primary-alt'"
            [type]="'reset'"
            [isDisabled]="!hasChanges"
            (click)="handleAbortChangesDialog()"
            [text]="cmsTextProvider.shared.cancel">
          </aw-button>
          <aw-button
            [id]="'save-entity'"
            [style]="'primary'"
            (click)="save()"
            [type]="'button'"
            [isDisabled]="!footerForm.valid || !hasChanges || isSaving()"
            [text]="cmsTextProvider.shared.save">
          </aw-button>
        </aw-section-header-actions>
      </aw-section-header>
      <div class="flex justify-end">
        <button
          type="button"
          class="text-primary-main hover:bg-standard-hover-main flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 font-semibold transition-colors"
          (click)="toggleAllSections()">
          {{
            areAllSectionsCollapsed()
              ? cmsTextProvider.configuration.expandAll
              : cmsTextProvider.configuration.collapseAll
          }}
          <span class="material-symbols-rounded text-xl!">
            {{ areAllSectionsCollapsed() ? 'unfold_more' : 'unfold_less' }}
          </span>
        </button>
      </div>
    </div>
    <div
      class="flex min-h-0 w-full flex-1 flex-col overflow-y-auto px-21 pb-5 [scrollbar-gutter:stable]">
      <div
        class="grid grid-cols-1 gap-4 py-5 lg:grid-cols-[12rem_minmax(0,1fr)]"
        data-testid="section-Appearance">
        <div class="pt-2 font-semibold">
          {{ cmsTextProvider.shared.appearance }}
        </div>
        <div class="min-w-0">
          <!-- Colors -->
          <div data-testid="section-Colors" class="flex flex-col gap-3">
            <div class="flex w-full flex-col gap-2 md:flex-row">
              <div
                class="border-standard-outline-light flex w-full flex-col gap-2 rounded-md border p-4">
                <div class="flex justify-end">
                  <div
                    class="bg-standard-surface text-md flex w-fit items-center gap-2 rounded-full px-2 py-1">
                    <span class="material-symbols-rounded cursor-pointer"
                      >light_mode</span
                    >
                    <span>{{ cmsTextProvider.footer.lightMode }}</span>
                  </div>
                </div>
                <div class="relative flex items-center gap-2">
                  <aw-color-picker-list
                    [buttonSize]="'lg'"
                    [color]="
                      footerForm.get('appearance')?.get('fontColor')?.value ??
                      ''
                    "
                    (colorChanged)="onColorChosen($event, 'fontColor')" />

                  <span>{{ cmsTextProvider.footer.font }}</span>
                </div>
                <div class="relative flex items-center gap-2">
                  <aw-color-picker-list
                    [buttonSize]="'lg'"
                    [color]="
                      footerForm.get('appearance')?.get('backgroundColor')
                        ?.value ?? ''
                    "
                    (colorChanged)="onColorChosen($event, 'backgroundColor')" />

                  <span>{{ cmsTextProvider.footer.background }}</span>
                </div>
              </div>
              <div
                class="border-standard-outline-light bg-primary-dark text-primary-on flex w-full flex-col gap-2 rounded-md border p-4">
                <div class="flex justify-end">
                  <div
                    class="bg-standard-surface text-md text-standard-text-primary flex w-fit items-center gap-2 rounded-full px-2 py-1">
                    <span class="material-symbols-rounded cursor-pointer"
                      >dark_mode</span
                    >
                    <span>{{ cmsTextProvider.footer.darkMode }}</span>
                  </div>
                </div>

                <div class="relative flex items-center gap-2">
                  <aw-color-picker-list
                    [buttonSize]="'lg'"
                    [color]="
                      footerForm.get('appearance')?.get('fontColorDark')
                        ?.value ?? ''
                    "
                    (colorChanged)="onColorChosen($event, 'fontColorDark')" />

                  <span>{{ cmsTextProvider.footer.font }}</span>
                </div>
                <div class="relative flex items-center gap-2">
                  <aw-color-picker-list
                    [buttonSize]="'lg'"
                    [color]="
                      footerForm.get('appearance')?.get('backgroundColorDark')
                        ?.value ?? ''
                    "
                    (colorChanged)="
                      onColorChosen($event, 'backgroundColorDark')
                    " />

                  <span>{{ cmsTextProvider.footer.background }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2" formGroupName="appearance">
              <aw-checkbox
                formControlName="showBackgroundImage"
                [label]="cmsTextProvider.shared.showBackgroundImage"
                id="showBackgroundImage">
              </aw-checkbox>
            </div>
            @if (
              footerForm.controls.appearance.controls.showBackgroundImage.value
            ) {
              <div
                class="flex w-full items-end gap-2"
                formGroupName="appearance">
                <div class="w-full">
                  <span
                    class="text-standard-text-secondary text-md mb-1 block font-semibold"
                    >{{ cmsTextProvider.footer.imageSource }}</span
                  >
                  <aw-image-select
                    class="w-full"
                    [id]="'footer-background-image'"
                    usageLabel="'footer.backgroundImage'"
                    formControlName="backgroundImage" />
                </div>

                <div class="w-full">
                  <span
                    class="text-standard-text-secondary text-md mb-1 block font-semibold"
                    >{{ cmsTextProvider.footer.description }}</span
                  >
                  <aw-accessibility-text-input
                    [id]="'backgroundImageAlt'"
                    [formControl]="
                      footerForm.controls.appearance.controls.backgroundImageAlt
                    "
                    [label]="''" />
                </div>
              </div>
            }
          </div>
        </div>
      </div>
      <!-- Layout -->
      <div
        class="border-standard-outline-light grid grid-cols-1 gap-4 border-b py-5 lg:grid-cols-[12rem_minmax(0,1fr)]"
        data-testid="section-Layout">
        <div class="pt-2 font-semibold">
          {{ cmsTextProvider.shared.layout }}
        </div>

        <div class="w-full">
          <aw-drag-drop-grid
            [blocks]="footerForm.controls.layout.controls.blockOrder.value"
            [customBlocks]="getCustomLayoutBlocks()"
            [hiddenBlocks]="
              footerForm.controls.layout.controls.hiddenBlocks.value
            "
            (blockOrderChanged)="onBlockOrderChanged($event)"
            (hiddenBlocksChanged)="onHiddenBlocksChanged($event)">
          </aw-drag-drop-grid>
        </div>
      </div>
      <!-- Blocks -->
      <div formArrayName="blocks" class="flex flex-col">
        @for (
          block of footerForm.controls.blocks.controls;
          track block;
          let index = $index
        ) {
          <aw-section
            class="border-standard-outline-light block border-b"
            [testIdAffix]="'block-' + index"
            [formGroupName]="index"
            [isCollapsible]="true"
            [withoutContentPadding]="true"
            [isInitiallyCollapsed]="false">
            <aw-section-header class="px-3 py-4">
              @let header = (block.controls.headlines.value | language).trim();
              <aw-section-headline size="small">
                {{
                  header ||
                    cmsTextProvider.footer.block + ' ' + getBlockLetter(index)
                }}
                <span class="material-symbols-rounded ml-1 align-middle">{{
                  footerBlockIcons.get(block.controls.type.value) ?? 'article'
                }}</span>
              </aw-section-headline>
              <aw-section-header-actions>
                <div class="flex items-center gap-2">
                  @if (
                    footerForm.controls.layout.controls.hiddenBlocks.value.includes(
                      block.controls.id.value ?? ''
                    )
                  ) {
                    <span
                      class="bg-standard-surface border-standard-outline-light text-standard-text-secondary h-fit rounded-full border px-2.5 py-1 text-xs font-semibold">
                      {{ cmsTextProvider.footer.hidden }}
                    </span>
                  } @else {
                    <span
                      class="bg-info-light text-info-main border-info-light h-fit rounded-full border px-2.5 py-1 text-xs font-semibold">
                      {{ cmsTextProvider.footer.visible }}
                    </span>
                  }
                </div>
              </aw-section-header-actions>
            </aw-section-header>
            <aw-footer-block-editor [block]="block" [index]="index" />
          </aw-section>
        }
      </div>
      <!-- LinkArea -->
      <aw-section
        class="border-standard-outline-light block border-b"
        formGroupName="linkArea"
        testIdAffix="linkArea"
        [isCollapsible]="true"
        [withoutContentPadding]="true"
        [isInitiallyCollapsed]="false">
        <aw-section-header class="px-3 py-4">
          <aw-section-headline size="small">
            {{ cmsTextProvider.footer.legalArea }}
            <span class="material-symbols-rounded ml-1 align-middle">
              {{
                footerBlockIcons.get(
                  footerForm.controls.linkArea.controls.type.value
                ) ?? 'article'
              }}
            </span>
          </aw-section-headline>
          <aw-section-header-actions>
            <span
              class="bg-info2-light text-info2-dark rounded-full px-2.5 py-1 text-xs font-semibold">
              {{ cmsTextProvider.footer.alwaysVisible }}
            </span>
          </aw-section-header-actions>
        </aw-section-header>

        <div class="flex flex-col gap-6 py-4">
          <!-- Links List -->
          <div
            class="grid grid-cols-1 gap-4 lg:grid-cols-[12rem_minmax(0,1fr)]">
            <div
              class="text-standard-text-secondary pt-3 pl-8 text-sm font-semibold">
              {{ cmsTextProvider.footer.links }}
            </div>
            <div class="flex w-full flex-col gap-4">
              @for (
                linkAreaForm of footerForm.controls.linkArea.controls.links
                  .controls;
                let index = $index;
                track linkAreaForm
              ) {
                <div class="flex items-start gap-4">
                  <div
                    class="flex min-h-12 flex-col items-center justify-center">
                    <button
                      type="button"
                      class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                      [disabled]="index === 0"
                      (click)="
                        moveItemAtIndex(
                          footerForm.controls.linkArea.controls.links,
                          index,
                          index - 1
                        )
                      ">
                      <span class="material-symbols-rounded"
                        >keyboard_arrow_up</span
                      >
                    </button>
                    <button
                      type="button"
                      class="disabled:text-standard-text-disabled text-info-main cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed"
                      [disabled]="
                        index ===
                        footerForm.controls.linkArea.controls.links.length - 1
                      "
                      (click)="
                        moveItemAtIndex(
                          footerForm.controls.linkArea.controls.links,
                          index,
                          index + 1
                        )
                      ">
                      <span class="material-symbols-rounded"
                        >keyboard_arrow_down</span
                      >
                    </button>
                  </div>
                  <div class="grid flex-1 grid-cols-2 gap-4">
                    <aw-translation-text-input
                      [entityType]="EntityType.footerLinkArea"
                      [attr.scroll-mark]="linkAreaForm.value.texts?.[0]?.id"
                      [relationType]="TextRelationType.text"
                      [id]="'linkText' + index"
                      [formControl]="linkAreaForm.controls.texts"
                      [label]="cmsTextProvider.footer.description">
                    </aw-translation-text-input>
                    <aw-translation-text-input
                      [entityType]="EntityType.footerLinkArea"
                      [attr.scroll-mark]="linkAreaForm.value.urls?.[0]?.id"
                      [relationType]="TextRelationType.url"
                      [id]="'linkAddress' + index"
                      [formControl]="linkAreaForm.controls.urls"
                      [label]="cmsTextProvider.footer.linkAddressLabel">
                    </aw-translation-text-input>
                  </div>

                  <div class="flex items-center gap-1">
                    @if (isMandatoryFooterLink(linkAreaForm)) {
                      <div class="relative">
                        <div
                          class="text-standard-text-secondary bg-standard-text-secondary/15 peer flex h-12 w-12 items-center justify-center rounded-md">
                          <span class="material-symbols-rounded text-sm"
                            >lock</span
                          >
                        </div>
                        <span
                          class="pointer-events-none absolute bottom-0 left-1/2 z-10 w-44 -translate-x-1/2 translate-y-full rounded-md bg-gray-800 px-1 text-xs text-gray-100 opacity-0 transition-opacity peer-hover:opacity-100">
                          {{ cmsTextProvider.footer.linkCannotBeRemoved }}
                        </span>
                      </div>
                    } @else {
                      <aw-button
                        class="block w-12 shrink-0 [&>button]:h-12! [&>button]:w-12! [&>button]:p-0!"
                        [id]="'delete-link-' + index"
                        type="button"
                        (click)="deleteLink(index)"
                        [style]="'error-alt'"
                        buttonSize="quadratic">
                        <span
                          class="material-symbols-rounded text-xl!"
                          aria-hidden="true"
                          >delete</span
                        >
                        <span class="sr-only">{{
                          cmsTextProvider.shared.delete
                        }}</span>
                      </aw-button>
                    }
                  </div>
                </div>
              }

              <div class="mt-2 flex justify-start">
                <aw-button
                  [id]="'add-legal-block'"
                  class="blue w-fit [&>button]:text-sm [&>button]:text-[#0066CC]!"
                  type="button"
                  [style]="'primary-alt'"
                  buttonSize="small"
                  (click)="addLink($event)">
                  <span
                    class="material-symbols-rounded mr-1 text-base!"
                    aria-hidden="true">
                    add
                  </span>
                  {{ cmsTextProvider.shared.add }}
                </aw-button>
              </div>
            </div>
          </div>
        </div>
      </aw-section>
    </div>
  </div>
}
